import React, { useContext, useEffect, useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import utils from '../../utils'
import SideBar from '../../components/houses/SideBar'
import HouseList from '../../components/houses/HouseList'
import houseServices from '../../services/HousesService'
import Loading from '../../components/feedback/Loading'
import FetchError from '../../components/feedback/FetchError'
import { useSearchParams } from 'react-router-dom'
import FilterContext from '../../context/FilterContext'
import MobileSideBar from '../../components/houses/MobileSideBar'
import Pagination from '../../components/nav/Pagination'


const HousePlansHero = () => {
    return (
        <section className="relative py-16 px-3 overflow-hidden bg-surface-container-low mb-16">
            <div
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                    backgroundImage: 'linear-gradient(#005baf 1px, transparent 1px), linear-gradient(90deg, #005baf 1px, transparent 1px)',
                    backgroundSize: '100px 100px'
                }}
            ></div>

            <div className="relative z-10 container px-3 mx-auto flex flex-col items-center text-center">
                <div className="inline-block px-4 py-1.5 mb-8 rounded-full bg-primary/10 border border-primary/20">
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">
                        Curated Architectural Designs
                    </span>
                </div>

                <h1 className="font-headline text-3xl md:text-5xl font-black text-on-surface tracking-tighter mb-8 leading-tight">
                    Modern Living, <br />
                    <span className="text-primary">Precision Engineering.</span>
                </h1>

                <p className="font-body text-xl text-on-surface-variant max-w-3xl leading-relaxed">
                    Browse our collection of professional architectural designs, engineered for
                    longevity and crafted to bridge the gap between visionary aesthetics and
                    structural reality.
                </p>

                <div className="mt-12 flex flex-wrap justify-center gap-4">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-outline-variant/20">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                        <span className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">NCA Compliant</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-outline-variant/20">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Ready for Construction</span>
                    </div>
                </div>
            </div>
            <div className="absolute -top-24 -right-24 w-96 h-36 bg-primary/5 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-96 h-36 bg-primary/5 rounded-full blur-3xl"></div>
        </section>
    );
};


function HousesListPage(props) {
    const [searchParam, setSearchParams] = useSearchParams()
    const [hasFiltered, setHasFiltered] = useState(false)
    const [houses, setHouses] = useState()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState()
    const categoryParam = searchParam.get("category__slug")
    const roomsParam = searchParam.get("bedrooms")
    const storiesParam = searchParam.get("stories")
    const bathroomsParam = searchParam.get("bathrooms")
    const pageParam = searchParam.get("page")
    const filterCtx = useContext(FilterContext)

    useEffect(() => {
        if (!hasFiltered) {
            filterCtx.setHouseCategory(categoryParam)
            filterCtx.setHouseRooms(parseInt(roomsParam))
            filterCtx.setHouseStories(parseInt(storiesParam))
            filterCtx.setHouseBathrooms(bathroomsParam)
        }
    }, [
        hasFiltered, categoryParam, roomsParam,
        filterCtx, storiesParam, bathroomsParam,
        pageParam
    ])
    useEffect(() => {
        const params = searchParam.toString()
        setLoading(true)
        houseServices.getHouses(params ? `?${params}` : '').then(
            res => {
                setHouses(res.data)
                setLoading(false)
            },
            err => {
                console.log(err);
                setError(err)
                setLoading(false)
            }
        )
    }, [searchParam])
    useEffect(() => {
        if (houses && houses?.count > 1) {
            const top = document.getElementById("nav-top")
            top.scrollIntoView({ behavior: "smooth" })
        }
    }, [houses])
    useEffect(() => { }, [loading])
    useEffect(() => { }, [error])

    function changeCategoryParam(value) {
        utils.changeUrlPageParam(
            value, "category__slug", hasFiltered,
            setHasFiltered, filterCtx, setSearchParams
        )
    }
    function changeRoomsParam(value) {
        utils.changeUrlPageParam(
            value, "bedrooms", hasFiltered,
            setHasFiltered, filterCtx, setSearchParams
        )
    }
    function changeStoriesParam(value) {
        utils.changeUrlPageParam(
            value, "stories", hasFiltered,
            setHasFiltered, filterCtx, setSearchParams
        )
    }
    function changeBathroomsParam(value) {
        utils.changeUrlPageParam(
            value, "bathrooms", hasFiltered,
            setHasFiltered, filterCtx, setSearchParams
        )
    }
    function changePageParam(page) {
        if (page) {
            setSearchParams((params) => {
                params.set("page", page)
                return params
            })
        } else {
            setSearchParams((params) => {
                params.delete("page")
                return params
            })
        }
    }
    return (
        <HelmetProvider>
            <Helmet>
                <title>Houses - {utils.siteName}</title>
            </Helmet>
            <HousePlansHero />
            <div className='container mx-auto mb-16 px-3'>
                <div className="md:flex md:gap-8">
                    <SideBar
                        changeCategoryParam={changeCategoryParam}
                        changeRoomsParam={changeRoomsParam}
                        changeStoriesParam={changeStoriesParam}
                        changeBathroomsParam={changeBathroomsParam}
                        storiesParam={storiesParam}
                        bathroomsParam={bathroomsParam}
                    />
                    <div className="w-100 md:w-8/12 lg:w-3/4">
                        {loading ? (
                            <Loading />
                        ) : (
                            <div>
                                {error ? (
                                    <FetchError error={error} />
                                ) : (
                                    <div className="">
                                        <div id='houses-list-top'></div>
                                        <HouseList
                                            houses={houses}
                                        />
                                        {((houses?.next !== null) || (houses?.previous !== null)) && (
                                            <Pagination
                                                next={houses?.next}
                                                previous={houses?.previous}
                                                changePageParam={changePageParam}
                                                isAdmin={false}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                </div>
                <div>
                    <MobileSideBar
                        changeCategoryParam={changeCategoryParam}
                        changeRoomsParam={changeRoomsParam}
                        changeStoriesParam={changeStoriesParam}
                        changeBathroomsParam={changeBathroomsParam}
                        storiesParam={storiesParam}
                        bathroomsParam={bathroomsParam}
                    />
                </div>
            </div>
        </HelmetProvider>
    )
}

export default HousesListPage
